import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { AppSettings } from "app/app.settings";
import { noResponseToastHeaders } from "@shared/constants";
import { TinaAddressResult } from "@modules/account/models/tina-location-result.model";
import { TinaStreetResult } from "@modules/account/models/tina-street-result.model";
import { TinaMunicipalityResult } from "@modules/account/models/tina-municipality-result.model";

@Injectable({
   providedIn: "root"
})
export class TinaLocationService {
   private httpClient = inject(HttpClient);
   private appSettings = inject(AppSettings);

   private readonly baseUrl: string;

   constructor() {
      this.baseUrl = `${this.appSettings.baseCoreApiUrl}/tina-location`;
   }

   public searchMunicipality(
      countryCode: string | undefined,
      postalCode: string
   ): Observable<Array<string>> {
      let params = new HttpParams().set("postalCode", postalCode);

      if (countryCode) {
         params = params.set("countryCode", countryCode);
      }

      return this.httpClient.get<Array<string>>(
         `${this.baseUrl}/municipality`,
         { params, headers: noResponseToastHeaders }
      );
   }

   public getMunicipalityByPostalCode(
      countryCode: string | undefined,
      postalCode: string | undefined
   ): Observable<TinaMunicipalityResult | undefined> {
      if (!postalCode) {
         return of(undefined);
      }

      let params = new HttpParams().set("postalCode", postalCode);

      if (countryCode) {
         params = params.set("countryCode", countryCode);
      }

      return this.httpClient.get<TinaMunicipalityResult>(
         `${this.baseUrl}/municipality/by-postal-code`,
         { params, headers: noResponseToastHeaders }
      );
   }

   public searchStreet(
      countryCode: string | undefined,
      postalCode: string,
      partialStreetName: string
   ): Observable<Array<TinaStreetResult>> {
      let params = new HttpParams()
         .set("postalCode", postalCode)
         .set("streetName", partialStreetName);

      if (countryCode) {
         params = params.set("countryCode", countryCode);
      }

      return this.httpClient.get<Array<TinaStreetResult>>(
         `${this.baseUrl}/street`,
         { params, headers: noResponseToastHeaders }
      );
   }

   public searchAddress(
      countryCode: string | undefined,
      postalCode: string,
      streetName: string,
      houseNumber: string,
      subHouseNumber?: string,
      boxNumber?: string
   ): Observable<Array<TinaAddressResult>> {
      let params = new HttpParams()
         .set("postalCode", postalCode)
         .set("streetName", streetName)
         .set("houseNumber", houseNumber);

      if (subHouseNumber) {
         params = params.set("subHouseNumber", subHouseNumber);
      }

      if (boxNumber) {
         params = params.set("boxNumber", boxNumber);
      }

      if (countryCode) {
         params = params.set("countryCode", countryCode);
      }

      return this.httpClient.get<Array<TinaAddressResult>>(
         `${this.baseUrl}/address`,
         { params, headers: noResponseToastHeaders }
      );
   }
}
